import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Copy, Check, ArrowRight, Plus, Trash2, User } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function BoardCreate() {
  const [form, setForm] = useState({ facilitator_name: "", facilitator_email: "", organisation: "", expected_participants: 6 });
  const [session, setSession] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError("");
    try {
      const { data } = await api.post("/board-sessions", form);
      setSession(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  if (session) {
    return <BoardManagement session={session} onFinish={() => navigate(`/board/${session.code}/results?token=${session.facilitator_token}`)} />;
  }

  return (
    <div className="min-h-screen bg-linen">
      <Navbar />
      <section className="max-w-4xl mx-auto px-6 lg:px-12 py-16">
        <div className="tick-label-accent mb-6">Board Session</div>
        <h1 className="display-serif text-5xl md:text-6xl max-w-2xl">Run the Index across your entire board.</h1>
        <p className="mt-6 text-lg text-charcoal max-w-xl">
          Every member completes the diagnostic independently. You receive an
          aggregated Board VRI — plus the Alignment Variance, revealing whether
          your board is even seeing the same organisation. Extend to direct
          reports and you also unlock the Leadership Cascade.
        </p>

        <form onSubmit={submit} className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6 border border-oxford/15 bg-linen p-8">
          <div className="md:col-span-2">
            <label className="tick-label block mb-2">Organisation name</label>
            <input data-testid="board-org-input" required value={form.organisation} onChange={e=>setForm({...form, organisation:e.target.value})} className="w-full border border-oxford/20 px-3 py-3 bg-parchment focus:outline-none focus:border-oxford" />
          </div>
          <div>
            <label className="tick-label block mb-2">Your name (facilitator)</label>
            <input data-testid="board-name-input" required value={form.facilitator_name} onChange={e=>setForm({...form, facilitator_name:e.target.value})} className="w-full border border-oxford/20 px-3 py-3 bg-parchment focus:outline-none focus:border-oxford" />
          </div>
          <div>
            <label className="tick-label block mb-2">Your email</label>
            <input data-testid="board-email-input" required type="email" value={form.facilitator_email} onChange={e=>setForm({...form, facilitator_email:e.target.value})} className="w-full border border-oxford/20 px-3 py-3 bg-parchment focus:outline-none focus:border-oxford" />
          </div>
          <div>
            <label className="tick-label block mb-2">Expected board members</label>
            <input data-testid="board-count-input" type="number" min="2" max="30" value={form.expected_participants} onChange={e=>setForm({...form, expected_participants:parseInt(e.target.value)||0})} className="w-full border border-oxford/20 px-3 py-3 bg-linen focus:outline-none focus:border-oxford font-mono" />
          </div>
          {error && <div className="md:col-span-2 text-heritage text-sm" data-testid="board-create-error">{error}</div>}
          <div className="md:col-span-2 mt-4">
            <button data-testid="board-create-submit" disabled={submitting} className="btn-orange">
              {submitting ? "Creating…" : "Create board session"} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5}/>
            </button>
          </div>
        </form>
      </section>
      <Footer />
    </div>
  );
}


function BoardManagement({ session, onFinish }) {
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", role: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [copiedKey, setCopiedKey] = useState("");
  const origin = window.location.origin;

  const refresh = async () => {
    const { data } = await api.get(`/board-sessions/${session.code}/members`, { params: { token: session.facilitator_token } });
    setMembers(data);
  };

  useEffect(() => { refresh(); }, [session.code]);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      await api.post(`/board-sessions/${session.code}/members`, form, { params: { token: session.facilitator_token } });
      setForm({ name: "", email: "", role: "" });
      await refresh();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    await api.delete(`/board-sessions/${session.code}/members/${id}`, { params: { token: session.facilitator_token } });
    await refresh();
  };

  const copy = async (t, key) => {
    await navigator.clipboard.writeText(t);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(""), 1400);
  };

  const facilitatorUrl = `${origin}/board/${session.code}/results?token=${session.facilitator_token}`;

  return (
    <div className="min-h-screen bg-linen">
      <Navbar />
      <section className="max-w-5xl mx-auto px-6 lg:px-12 pt-16 pb-8">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="tick-label-accent mb-6">Board session created</div>
          <h1 className="display-serif text-5xl md:text-6xl">
            Session <span className="text-orange">{session.code}</span>
          </h1>
          <p className="mt-4 text-charcoal max-w-2xl">
            Add each board director below. Every director gets their own private hub, where they can take the Index and forward an invitation to their direct reports — unlocking the Leadership Cascade.
          </p>
        </motion.div>

        <div className="mt-12 border border-oxford/15 bg-linen p-8" data-testid="board-facilitator-url-box">
          <div className="tick-label-accent mb-2">Facilitator results URL · keep private</div>
          <div className="flex gap-2">
            <input readOnly value={facilitatorUrl} className="flex-1 border border-oxford/20 bg-linen px-4 py-3 font-mono text-sm" />
            <button data-testid="board-copy-facilitator" onClick={() => copy(facilitatorUrl, "fac")} className="btn-ghost">
              {copiedKey === "fac" ? <Check className="w-4 h-4"/> : <Copy className="w-4 h-4"/>}
            </button>
          </div>
        </div>

        <div className="mt-16">
          <div className="flex items-baseline justify-between mb-6">
            <div>
              <div className="tick-label-accent">Board Members</div>
              <div className="display-serif text-3xl mt-1">Add your directors</div>
            </div>
            <div className="tick-label" data-testid="members-count">{members.length} added</div>
          </div>

          <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-12 gap-3 bg-linen border border-oxford/15 p-4">
            <input data-testid="member-name-input" required value={form.name} onChange={e=>setForm({...form, name:e.target.value})} placeholder="Name" className="md:col-span-4 border border-oxford/20 px-3 py-3 bg-parchment focus:outline-none focus:border-oxford" />
            <input data-testid="member-email-input" required type="email" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} placeholder="Email" className="md:col-span-4 border border-oxford/20 px-3 py-3 bg-parchment focus:outline-none focus:border-oxford" />
            <input data-testid="member-role-input" value={form.role} onChange={e=>setForm({...form, role:e.target.value})} placeholder="Role (optional)" className="md:col-span-3 border border-oxford/20 px-3 py-3 bg-parchment focus:outline-none focus:border-oxford" />
            <button data-testid="member-add-submit" disabled={saving} className="btn-orange md:col-span-1 justify-center px-2">
              <Plus className="w-4 h-4"/>
            </button>
            {error && <div className="md:col-span-12 text-heritage text-sm" data-testid="member-add-error">{error}</div>}
          </form>

          <div className="mt-6 space-y-3">
            <AnimatePresence initial={false}>
              {members.map((m, i) => {
                const memberUrl = `${origin}/team/${m.own_token}`;
                return (
                  <motion.div
                    key={m.id}
                    layout
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -30 }}
                    transition={{ duration: 0.35, delay: i * 0.03 }}
                    className="border border-oxford/15 bg-linen p-5"
                    data-testid={`member-row-${i}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-baseline gap-4">
                          <div className="display-serif text-2xl text-oxford">{m.name}</div>
                          <div className="tick-label">{m.role || "Director"}</div>
                          {m.own_completed && <div className="tick-label-accent">✓ own completed</div>}
                          {m.team_count > 0 && <div className="tick-label">team: {m.team_count}</div>}
                        </div>
                        <div className="text-sm text-charcoal mt-1">{m.email}</div>
                        <div className="mt-4 flex gap-2 items-center">
                          <div className="tick-label whitespace-nowrap">Their hub URL</div>
                          <input readOnly value={memberUrl} className="flex-1 border border-oxford/15 bg-linen px-3 py-2 font-mono text-xs" />
                          <button data-testid={`member-copy-${i}`} onClick={() => copy(memberUrl, `m${i}`)} className="btn-ghost px-3 py-2">
                            {copiedKey === `m${i}` ? <Check className="w-3.5 h-3.5"/> : <Copy className="w-3.5 h-3.5"/>}
                          </button>
                        </div>
                      </div>
                      <button data-testid={`member-remove-${i}`} onClick={() => remove(m.id)} className="text-charcoal hover:text-heritage p-2">
                        <Trash2 className="w-4 h-4" strokeWidth={1.5}/>
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
            {members.length === 0 && (
              <div className="border border-dashed border-oxford/20 p-10 text-center text-charcoal text-sm">
                <User className="inline w-4 h-4 mr-2" strokeWidth={1.5}/> No directors added yet — start with your Chair.
              </div>
            )}
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-oxford/10 flex flex-wrap gap-4">
          <button data-testid="board-view-results" onClick={onFinish} className="btn-orange">
            View live results <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5}/>
          </button>
          <Link to="/" className="btn-ghost">Return home</Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
